import React, { useState, useEffect } from 'react';
import { Button, Table, Modal, Form, Card, Nav, Badge } from 'react-bootstrap';
import ApiService from '../../helpers/apiService';
import Select from 'react-select';
import EmailTemplateBuilder from './email-templates/EmailTemplateBuilder';
import {
  EmailBlocks,
  EmailTemplateConfig,
  configToBlocks,
  getEmailPresets,
  renderBlocks,
} from './email-templates/baseTemplate';
import { useProject } from '../../contexts/ProjectContext';

interface Template {
  id: string;
  mnemonic: string;
  parameters: string[];
  message: string;
}

const parametersList = [
  { label: 'Наименование пользователя', value: 'name' },
  { label: 'Сумма', value: 'amount' },
  { label: 'Валюта', value: 'currency' },
  { label: 'Комиссия', value: 'fee' },
  { label: 'Номер транзакции', value: 'trx_num' },
  { label: 'Лимит', value: 'limit' },
  { label: 'Остаток по лимиту', value: 'limit_remain' },
  { label: 'Причина', value: 'reason' },
  { label: 'Срок', value: 'duration' },
  { label: 'Отправитель', value: 'send_tmp' },
  { label: 'OTP-код', value: 'otp_code' },
  { label: 'Устройство', value: 'device' },
  { label: 'Последние 4 цифры карты', value: 'card_last4' },
];

/** Дефолтные текстовые шаблоны (push/sms) */
const DEFAULT_TEXT_TEMPLATES: Template[] = [
  { id: 'welcome', mnemonic: 'welcome', parameters: ['name'], message: 'Welcome to Trientes, %%name%%! Your account has been created successfully.' },
  { id: 'otp_code', mnemonic: 'otp_code', parameters: ['otp_code', 'duration'], message: 'Your verification code: %%otp_code%%. Valid for %%duration%%.' },
  { id: 'kyc_approved', mnemonic: 'kyc_approved', parameters: ['name'], message: '%%name%%, your KYC verification has been approved. All platform features are now unlocked.' },
  { id: 'kyc_rejected', mnemonic: 'kyc_rejected', parameters: ['name', 'reason'], message: '%%name%%, your verification was declined. Reason: %%reason%%. Please try again.' },
  { id: 'deposit', mnemonic: 'deposit', parameters: ['name', 'amount', 'currency'], message: '%%name%%, your account has been credited with %%amount%% %%currency%%.' },
  { id: 'withdrawal', mnemonic: 'withdrawal', parameters: ['name', 'amount', 'currency', 'trx_num'], message: '%%name%%, your withdrawal of %%amount%% %%currency%% has been processed. Transaction: %%trx_num%%.' },
  { id: 'card_issued', mnemonic: 'card_issued', parameters: ['name', 'card_last4'], message: '%%name%%, your virtual card *%%card_last4%% is ready to use.' },
  { id: 'security_alert', mnemonic: 'security_alert', parameters: ['name', 'device'], message: '%%name%%, a new login was detected from %%device%%. If this was not you, please change your password.' },
  { id: 'password_reset', mnemonic: 'password_reset', parameters: ['name', 'duration'], message: '%%name%%, a password reset was requested. The link is valid for %%duration%%.' },
  { id: 'transaction_alert', mnemonic: 'transaction_alert', parameters: ['amount', 'currency', 'fee', 'trx_num'], message: 'Transaction of %%amount%% %%currency%% (fee %%fee%%). Transaction ID: %%trx_num%%.' },
  { id: 'account_blocked', mnemonic: 'account_blocked', parameters: ['name', 'reason'], message: '%%name%%, your account has been temporarily restricted. Reason: %%reason%%.' },
];

const getParamsObject = (parameters: string[]) => {
  const paramsObject: { [key: string]: string } = {};
  parameters.forEach(param => {
    const paramInfo = parametersList.find(p => p.value === param);
    if (paramInfo) {
      paramsObject[param] = paramInfo.label;
    } else {
      paramsObject[param] = param;
    }
  });
  return paramsObject;
};

interface EmailTemplate {
  id: string;
  mnemonic: string;
  /** v56: блочная структура. Опционально для backward compat (legacy записи). */
  blocks?: EmailBlocks;
  /** Legacy: старый формат полей. Используется как fallback пока миграция не прогналась. */
  config?: EmailTemplateConfig;
  /** Пререндеренный HTML (читается админкой для thumbnail-preview). */
  html: string;
}

/**
 * Возвращает blocks для шаблона: из новой колонки, либо мигрирует из legacy config.
 * Используется и в редакторе (чтобы открыть EmailTemplateBuilder), и в превью.
 */
function getTemplateBlocks(t: EmailTemplate, assetsBaseUrl?: string): EmailBlocks | null {
  if (t.blocks) return t.blocks;
  if (t.config) return configToBlocks(t.config, assetsBaseUrl);
  return null;
}

const Templates: React.FC = () => {
  const { currentProjectId, currentProject } = useProject();
  const assetsBaseUrl = (currentProject as any)?.project_data?.assets_base_url;
  const [activeTab, setActiveTab] = useState<'text' | 'email'>('text');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [currentTemplate, setCurrentTemplate] = useState<Template | null>(null);
  const [modalType, setModalType] = useState<'add' | 'edit'>('add');
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [editingEmailTemplate, setEditingEmailTemplate] = useState<EmailTemplate | null>(null);

  const [mnemonic, setMnemonic] = useState<string>('');
  const [selectedParameters, setSelectedParameters] = useState<string[]>([]);
  const [message, setMessage] = useState<string>('');

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await ApiService.getTemplates();
      const apiTemplates = (data.data || []).map((template: any) => ({
        id: template.id,
        mnemonic: template.mnemonic || template.id,
        parameters: Object.keys(template.params || {}),
        message: template.content
      }));

      if (apiTemplates.length > 0) {
        setTemplates(apiTemplates);
      } else {
        // API вернул пустой массив (или notifications-сервис недоступен —
        // admin-api fail-soft, см. NOTIF-001 в backlog). Показываем дефолтные
        // шаблоны локально БЕЗ автосида — иначе N+1 запросов addTemplate
        // тоже упадут и забьют консоль ошибками.
        setTemplates(DEFAULT_TEXT_TEMPLATES);
      }
    } catch (error) {
      console.error('Ошибка при загрузке шаблонов:', error);
      // Fallback — дефолтные шаблоны без API
      setTemplates(DEFAULT_TEXT_TEMPLATES);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const storageKey = currentProjectId ? `email-templates-v1-${currentProjectId}` : 'email-templates-v1';
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.length > 0) {
          setEmailTemplates(parsed);
          return;
        }
      }
      // Seed дефолтные email-шаблоны: legacy config → blocks через configToBlocks.
      // В будущем (Phase 2) пресеты переедут в sender.templates.blocks_json напрямую.
      const presets = getEmailPresets(assetsBaseUrl);
      const defaults: EmailTemplate[] = Object.entries(presets).map(([key, config]) => {
        const blocks = configToBlocks(config, assetsBaseUrl);
        return {
          id: key,
          mnemonic: key,
          blocks,
          html: renderBlocks(blocks),
        };
      });
      saveEmailTemplates(defaults);
    } catch (e) {
      console.error('Ошибка загрузки email-шаблонов:', e);
    }
  };

  const saveEmailTemplates = (list: EmailTemplate[]) => {
    setEmailTemplates(list);
    const storageKey = currentProjectId ? `email-templates-v1-${currentProjectId}` : 'email-templates-v1';
    localStorage.setItem(storageKey, JSON.stringify(list));
  };

  /**
   * Сохраняет email-шаблон из EmailTemplateBuilder.
   * Новый формат — blocks; html пересобирается при каждом save для thumbnail-preview
   * (рендер дёшёвый, делаем на фронте чтобы не дёргать sender при превью).
   */
  const handleSaveEmailTemplate = (mnemo: string, blocks: EmailBlocks) => {
    const html = renderBlocks(blocks);
    const entry: EmailTemplate = { id: mnemo, mnemonic: mnemo, blocks, html };
    const existing = emailTemplates.findIndex(t => t.mnemonic === (editingEmailTemplate?.mnemonic || mnemo));
    if (existing >= 0) {
      const updated = [...emailTemplates];
      updated[existing] = entry;
      saveEmailTemplates(updated);
    } else {
      saveEmailTemplates([...emailTemplates, entry]);
    }
    setShowEmailEditor(false);
    setEditingEmailTemplate(null);
  };

  const handleDeleteEmailTemplate = (mnemo: string) => {
    saveEmailTemplates(emailTemplates.filter(t => t.mnemonic !== mnemo));
  };

  useEffect(() => {
    fetchTemplates();
    fetchEmailTemplates();
  }, [currentProjectId]);

  useEffect(() => {
    if (currentTemplate) {
      setMnemonic(currentTemplate.mnemonic);
      setSelectedParameters(currentTemplate.parameters || []);
      setMessage(currentTemplate.message);
    } else {
      setMnemonic('');
      setSelectedParameters([]);
      setMessage('');
    }
  }, [currentTemplate]);

  const handleAddTemplate = () => {
    setModalType('add');
    setCurrentTemplate(null);
    setShowModal(true);
  };

  const handleEditTemplate = (template: Template) => {
    setModalType('edit');
    setCurrentTemplate(template);
    setShowModal(true);
  };

  const handleDeleteTemplate = async (id: string) => {
    try {
      await ApiService.deleteTemplate(id);
      setTemplates(templates.filter(template => template.id !== id));
    } catch (error) {
      console.error('Ошибка при удалении шаблона:', error);
    }
  };

  const handleParameterChange = (selectedOptions: any) => {
    const newSelectedParameters = selectedOptions
      ? selectedOptions.map((option: any) => option.value)
      : [];

    const newlySelectedParams = newSelectedParameters.filter(
      (param: string) => !selectedParameters.includes(param)
    );

    let updatedMessage = message;
    newlySelectedParams.forEach((param: string) => {
      if (!updatedMessage.includes(`%%${param}%%`)) {
        updatedMessage += ` %%${param}%%`;
      }
    });

    setMessage(updatedMessage);
    setSelectedParameters(newSelectedParameters);
  };

  const handleModalSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newTemplate: Template = {
      id: mnemonic, // Use the new mnemonic as the ID
      mnemonic: mnemonic,
      parameters: selectedParameters,
      message: message
    };

    const paramsObject = getParamsObject(newTemplate.parameters);

    try {
      if (modalType === 'add') {
        await ApiService.addTemplate({
          id: newTemplate.id,
          mnemonic: mnemonic,
          content: newTemplate.message,
          params: paramsObject,
        });
        fetchTemplates();
      } else {
        if (currentTemplate && currentTemplate.id !== mnemonic) {
          // Mnemonic has changed, delete old and add new
          await ApiService.deleteTemplate(currentTemplate.id);
          await ApiService.addTemplate({
            id: newTemplate.id,
            mnemonic: mnemonic,
            content: newTemplate.message,
            params: paramsObject
          });
        } else {
          // Mnemonic hasn't changed, proceed with update
          await ApiService.updateTemplate({
            id: currentTemplate?.id,
            mnemonic: mnemonic,
            content: newTemplate.message,
            params: paramsObject
          });
        }
        // Update the templates list
        fetchTemplates();
      }
      setShowModal(false);
    } catch (error) {
      console.error('Ошибка при сохранении шаблона:', error);
    }
  };

  return (
    <div className="Templates-page">
      <Card className="mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center py-2">
          <div>
            <h5 className="mb-0">Шаблоны сообщений</h5>
            <small className="text-muted">Текстовые и email-шаблоны для отправки уведомлений</small>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {(currentProject as any)?.name && <Badge bg="dark">{(currentProject as any).name}</Badge>}
            <Badge bg="primary">{templates.length} текст.</Badge>
            <Badge bg="info">{emailTemplates.length} email</Badge>
          </div>
        </Card.Body>
      </Card>

      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link active={activeTab === 'text'} onClick={() => setActiveTab('text')}>
            Текстовые шаблоны
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link active={activeTab === 'email'} onClick={() => setActiveTab('email')}>
            Email-шаблоны <Badge bg="info" className="ms-1">{emailTemplates.length}</Badge>
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {activeTab === 'text' && (
        <>
          <div className="mb-3 d-flex justify-content-end">
            <Button variant="primary" size="sm" onClick={handleAddTemplate}>+ Добавить</Button>
          </div>
          <Card>
            <Card.Body style={{ padding: 0 }}>
              {loading ? (
                <p className="p-3">Загрузка...</p>
              ) : (
                <Table hover responsive size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Мнемоника</th>
                      <th>Параметры</th>
                      <th>Сообщение</th>
                      <th style={{ width: 100 }}>Управление</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map(template => (
                      <tr key={template.id}>
                        <td><code>{template.mnemonic}</code></td>
                        <td>
                          {(template.parameters || []).map(p => (
                            <Badge key={p} bg="secondary" className="me-1 fw-normal">{p}</Badge>
                          ))}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 300 }}>{template.message}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button variant="outline-secondary" size="sm" onClick={() => handleEditTemplate(template)}>Ред.</Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteTemplate(template.id)}>Del</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {activeTab === 'email' && !showEmailEditor && (
        <>
          <div className="mb-3 d-flex justify-content-end">
            <Button variant="primary" size="sm" onClick={() => { setEditingEmailTemplate(null); setShowEmailEditor(true); }}>
              + Создать email-шаблон
            </Button>
          </div>
          {emailTemplates.length === 0 ? (
            <Card><Card.Body className="text-center text-muted py-5">
              Нет email-шаблонов. Нажмите «Создать email-шаблон» для начала.
            </Card.Body></Card>
          ) : (
            <div className="row g-3">
              {emailTemplates.map(et => (
                <div key={et.mnemonic} className="col-md-4">
                  <Card>
                    <div style={{ background: '#1a1a2e', borderRadius: '8px 8px 0 0', overflow: 'hidden', height: 200 }}>
                      <div style={{ transform: 'scale(0.33)', transformOrigin: 'top center' }}>
                        <div dangerouslySetInnerHTML={{ __html: et.html }} />
                      </div>
                    </div>
                    <Card.Body className="py-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <code>{et.mnemonic}</code>
                          <div className="small text-muted">
                            {et.blocks?.body.title || et.config?.title || ''}
                          </div>
                        </div>
                        <div className="d-flex gap-1">
                          <Button variant="outline-secondary" size="sm" onClick={() => {
                            setEditingEmailTemplate(et);
                            setShowEmailEditor(true);
                          }}>Ред.</Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteEmailTemplate(et.mnemonic)}>Del</Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'email' && showEmailEditor && (() => {
        // Подбор blocks для редактора: при редактировании — из шаблона (или мигрируем из legacy config),
        // при добавлении — дефолт с ассетами текущего проекта.
        const initialBlocks = editingEmailTemplate
          ? getTemplateBlocks(editingEmailTemplate, assetsBaseUrl)
          : null;
        return (
          <EmailTemplateBuilder
            mnemonic={editingEmailTemplate?.mnemonic || ''}
            blocks={initialBlocks || configToBlocks(getEmailPresets(assetsBaseUrl).welcome, assetsBaseUrl)}
            assetsBaseUrl={assetsBaseUrl}
            onSave={handleSaveEmailTemplate}
            onCancel={() => { setShowEmailEditor(false); setEditingEmailTemplate(null); }}
          />
        );
      })()}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Form onSubmit={handleModalSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalType === 'add' ? 'Добавить шаблон' : 'Редактировать шаблон'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group controlId="mnemonic" className="mb-3">
              <Form.Label className="fw-semibold">
                Title (мнемоника) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="mnemonic"
                value={mnemonic}
                onChange={e => setMnemonic(e.target.value)}
                pattern="^[a-zA-Z0-9_]+$"
                placeholder="welcome_push, kyc_approved_push, deposit_push, …"
                size="lg"
                required
              />
              <Form.Text className="text-muted">
                <strong className="text-warning">Обязательно для нотификаций и пушей.</strong> Это поле — идентификатор шаблона
                в коде сервисов (users / kyc / accounts / crypto-gateway).
                Сервис ищет шаблон по этому значению (например <code>welcome_push</code>),
                если не находит — пользователь получит дефолтный русский текст.
                Только латинские буквы, цифры и нижнее подчёркивание. <em>Изменение
                существующего title сломает уже работающие интеграции.</em>
              </Form.Text>
            </Form.Group>
            <Form.Group controlId="parameters" className="mb-3">
              <Form.Label>Параметры</Form.Label>
              <Select
                isMulti
                value={selectedParameters.map(value => {
                  const param = parametersList.find(p => p.value === value);
                  return { value: value, label: param ? param.label : value };
                })}
                onChange={handleParameterChange}
                options={parametersList}
                closeMenuOnSelect={false}
              />
              <Form.Text className="text-muted">
                Параметры подставляются в шаблон через <code>%%name%%</code>.
              </Form.Text>
            </Form.Group>
            <Form.Group controlId="message" className="mb-2">
              <Form.Label className="fw-semibold">
                Сообщение <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={"Для push: первая строка — заголовок, остальное — текст.\nНапример:\nWelcome to Trientes!\nHi %%name%%, your account is ready."}
                required
              />
              <Form.Text className="text-muted">
                Для <strong>push-нотификаций</strong>: первая строка — заголовок,
                остальное — текст уведомления. Параметры — <code>%%key%%</code>.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Отмена
            </Button>
            <Button variant="primary" type="submit">
              Сохранить
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Templates;
